'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { ComponentProps, useMemo, useState } from 'react';
import useUser from '@alltrails/context/hooks/useUser';
import type { Review } from '@alltrails/types/Review';
import { LOCATION_SECTION_ID_REVIEWS } from '@/constants/locationSectionIds';
import { LocationDetailsResponseType, PageFilterType, ReviewAndTrail, VisitorStats } from '@/types/locationDetailsPages/shared';
import layoutStyles from '../layouts/layoutStyles.module.scss';
import styles from './Reviews.module.scss';
import ReviewsList from './ReviewsList';
import { ReviewTitle } from './ReviewTitle';
import SocialProofing from './SocialProofing';

const ReviewFormModal = dynamic(() => import('@alltrails/reviews/components/NewReviewFormModal/NewReviewFormModal'));

type Props = {
  reviewsData: ReviewAndTrail[];
  mentionReviewSnippets?: Review[];
  locationName: string;
  type: LocationDetailsResponseType;
  visitorStats?: VisitorStats;
  pageFilterUid?: PageFilterType;
  isBot?: boolean;
  isScrollLimitActive?: boolean;
};

const Reviews = ({ reviewsData, mentionReviewSnippets = [], locationName, type, visitorStats, pageFilterUid, isBot }: Props) => {
  const user = useUser();
  const reviews = useMemo(() => reviewsData?.map?.(review => review.review_snippet) ?? [], [reviewsData]);
  const [isReviewFormOpen, toggleReviewForm] = useState(false);
  const [reviewFormProps, setReviewFormProps] = useState<ComponentProps<typeof ReviewFormModal>>();
  const [currentTrailId, setCurrentTrailId] = useState<number | undefined>();
  const openReviewFormModal = (formProps: ComponentProps<typeof ReviewFormModal>) => {
    setReviewFormProps(formProps);
    toggleReviewForm(true);
    setCurrentTrailId(formProps.trailId);
  };

  const [displayReviews, displayMentionReviews] = useMemo(() => {
    let displayReviews = reviews;
    let displayMentionReviews = mentionReviewSnippets;

    return [displayReviews, displayMentionReviews];
  }, [reviews, mentionReviewSnippets]);

  return (
    <div id={LOCATION_SECTION_ID_REVIEWS}>
      <div className={styles.container}>
        <span className={styles.overview}>
          <h2 className={styles.reviewTitle}>
            <ReviewTitle locationName={locationName} type={type} pageFilterUid={pageFilterUid} />
          </h2>
          {visitorStats && type === 'park' && <SocialProofing reviews={[...mentionReviewSnippets, ...reviews]} visitorStats={visitorStats} />}
        </span>
        <div className={classNames(layoutStyles.col1, styles.desktopSpacer)} />
        <ReviewsList
          type={type}
          locationName={locationName}
          openReviewFormModal={openReviewFormModal}
          reviews={displayReviews}
          mentionReviews={displayMentionReviews}
          isBot={isBot}
        />
        {user && isReviewFormOpen && (
          <ReviewFormModal
            activity={reviewFormProps?.activity}
            associatedRecording={reviewFormProps?.associatedRecording}
            associatedTrailReviewId={reviewFormProps?.associatedTrailReviewId}
            closeReviewModal={() => toggleReviewForm(false)}
            comment={reviewFormProps?.comment}
            comment_source={reviewFormProps?.comment_source}
            date={reviewFormProps?.date}
            difficulty={reviewFormProps?.difficulty}
            id={reviewFormProps?.id}
            infoAttributes={reviewFormProps?.infoAttributes}
            isActivity={reviewFormProps?.isActivity}
            obstacles={reviewFormProps?.obstacles}
            rating={reviewFormProps?.rating}
            ratingAttributes={reviewFormProps?.ratingAttributes}
            reviewSource={reviewFormProps?.reviewSource}
            trailId={currentTrailId}
            user={user}
          />
        )}
      </div>
    </div>
  );
};

export default Reviews;
